import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Loader from "@amzn/meridian/loader";
import Alert from "@amzn/meridian/alert";
import PorApiFactory from "../../por-api/PlanOfRecordApiFactory";
import { ActiveSiteData } from "../../por-api/generated-src";
import { useBulkEditContext, BulkEditRow, EditableFieldData } from "../../store/bulk-edit-store";
import { usePORContext } from "../../store/por-context";
import { Logger } from "@amzn/kepler-katal-logger";
import { ALERT_AUTHENTICATION_MESSAGE, POR_SUPER_ADMIN } from "../../constants";
import { BULK_EDIT_IDENTIFIER_COLUMNS, FIELD_GROUP_BUILDING_ATTRIBUTES, FIELD_GROUP_PRODUCTIVITY } from "./BulkEditConstants";
import BulkEditTable from "./BulkEditTable";
import BulkEditPagination from "./BulkEditPagination";
import "./BulkEditPage.scss";

export default function BulkEditPage() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { user } = usePORContext();
  const {
    rows,
    setRows,
    setOriginalRows,
    visibleColumns,
    setVisibleColumns,
    isLoading,
    setIsLoading,
  } = useBulkEditContext();

  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const PorApi = PorApiFactory();
  const isSuperAdmin = true; // TODO: revert to `user === POR_SUPER_ADMIN` before prod

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const selectedProjectIds: string[] = locationState?.selectedProjectIds || [];
    if (!selectedProjectIds.length) {
      navigate("/active-projects");
      return;
    }

    loadProjectData(selectedProjectIds);
  }, []);

  const loadProjectData = async (projectIds: string[]) => {
    setIsLoading(true);
    try {
      const projectPromises = projectIds.map((id) => PorApi.getActiveSite(id));
      const responses = await Promise.all(projectPromises);
      const bulkEditRows: BulkEditRow[] = responses.map((response) => {
        const projectData = response.data as ActiveSiteData;
        return transformToRow(projectData);
      });

      setRows(bulkEditRows);
      setOriginalRows(bulkEditRows);

      // Set all editable columns visible by default
      if (bulkEditRows.length > 0) {
        const allEditableColumns = Object.keys(bulkEditRows[0].editableFields);
        setVisibleColumns(allEditableColumns);
      }
    } catch (ex: any) {
      if (ex.response?.status === 403) {
        setErrorMessage(ALERT_AUTHENTICATION_MESSAGE);
      } else {
        setErrorMessage(ex.message || "Failed to load project data.");
        Logger.getInstance().logError(ex);
      }
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const transformToRow = (projectData: ActiveSiteData): BulkEditRow => {
    const identifierFields: Record<string, string> = {};
    const editableFields: Record<string, EditableFieldData> = {};

    // Search ALL field groups for identifier columns
    const allFieldGroups = [
      ...(projectData.siteDetails || []),
      ...(projectData.buildingAttributes || []),
      ...(projectData.productivity || []),
      ...(projectData.additionalDetails || []),
      ...(projectData.retrofitDetails || []),
    ];

    for (const field of allFieldGroups) {
      if (BULK_EDIT_IDENTIFIER_COLUMNS.includes(field.fieldName as any)) {
        identifierFields[field.fieldName || ""] = field.value || "";
      }
    }

    // Extract editable fields from buildingAttributes
    for (const field of projectData.buildingAttributes || []) {
      editableFields[field.fieldName || ""] = {
        value: field.value || "",
        originalValue: field.value || "",
        fieldType: field.type || "text",
        required: field.required || false,
        options: field.options,
        fieldGroup: FIELD_GROUP_BUILDING_ATTRIBUTES,
      };
    }

    // Extract editable fields from productivity
    for (const field of projectData.productivity || []) {
      editableFields[field.fieldName || ""] = {
        value: field.value || "",
        originalValue: field.value || "",
        fieldType: field.type || "text",
        required: field.required || false,
        options: field.options,
        fieldGroup: FIELD_GROUP_PRODUCTIVITY,
      };
    }

    return {
      projectId: projectData.projectId || "",
      identifierFields,
      editableFields,
    };
  };

  const navigateBack = () => {
    navigate("/active-projects");
  };

  if (!isSuperAdmin) {
    return (
      <Column width="100%" spacingInset="400">
        <Alert type="error">
          You do not have permission to access Bulk Edit. Super Admin access is required.
        </Alert>
      </Column>
    );
  }

  if (isLoading) {
    return (
      <Column width="100%" spacingInset="400" alignmentHorizontal="center">
        <Loader size="large" label="Loading project data..." />
      </Column>
    );
  }

  return (
    <Column width="100%" spacingInset="400" className="bulk-edit-page">
      {showAlert && (
        <Alert type="error" onClose={() => setShowAlert(false)}>
          {errorMessage}
        </Alert>
      )}
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Row alignmentVertical="center">
          <Heading level={4}>Bulk Edit ({rows.length} projects)</Heading>
        </Row>
        <Row>
          {/* Column Filter button will be added in Phase 4 */}
          {/* Save button will be added in Phase 4 */}
          <Button type="tertiary" onClick={navigateBack}>
            Return to Projects
          </Button>
        </Row>
      </Row>
      <BulkEditTable />
      <BulkEditPagination />
    </Column>
  );
}
